import React from "react";
import { useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import ArrowForward from "@mui/icons-material/ArrowForward";

/* ───────── Animations ───────── */
const shimmer = keyframes`
  0%   { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ───────── Styles ───────── */
const Section = styled.div`
  max-width: 1100px;
  margin: 60px auto;
  padding: 0 20px;
  animation: ${slideUp} 0.6s ease-out;
`;

const Card = styled.div`
  display: flex;
  align-items: center;
  gap: 40px;
  background: ${({ theme }) => theme.card};
  border-radius: 20px;
  padding: 32px;
  border: 1px solid rgba(${({ theme }) => theme.primaryRgb}, 0.12);
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.2);
  overflow: hidden;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 24px;
    padding: 20px;
  }
`;

const ImageSide = styled.div`
  flex: 0 0 45%;
  border-radius: 14px;
  overflow: hidden;
  position: relative;
  box-shadow: 0 6px 24px rgba(${({ theme }) => theme.primaryRgb}, 0.2);

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      135deg,
      rgba(${({ theme }) => theme.primaryRgb}, 0.15) 0%,
      transparent 60%
    );
    pointer-events: none;
  }

  img {
    width: 100%;
    display: block;
    aspect-ratio: 4 / 3;
    object-fit: cover;
    transition: transform 0.5s ease;
  }

  &:hover img {
    transform: scale(1.04);
  }

  @media (max-width: 768px) {
    flex: none;
    width: 100%;
  }
`;

const ContentSide = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
`;

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(${({ theme }) => theme.primaryRgb}, 0.1);
  border: 1px solid rgba(${({ theme }) => theme.primaryRgb}, 0.25);
  color: ${({ theme }) => theme.primary};
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  width: fit-content;
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

const Title = styled.h2`
  font-size: 28px;
  font-weight: 700;
  color: ${({ theme }) => theme.text_primary};
  line-height: 1.25;
  margin: 0;

  span {
    background: linear-gradient(135deg, ${({ theme }) => theme.primary}, ${({ theme }) => theme.primaryLight}, ${({ theme }) => theme.primary});
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: ${shimmer} 3s linear infinite;
  }

  @media (max-width: 768px) {
    font-size: 22px;
  }
`;

const StoryText = styled.p`
  font-size: 14px;
  line-height: 1.75;
  color: ${({ theme }) => theme.text_secondary};
  margin: 0;

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const ExploreButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
  padding: 12px 28px;
  background: ${({ theme }) => theme.primaryGradient135};
  color: white;
  border: none;
  border-radius: 30px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  width: fit-content;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(${({ theme }) => theme.primaryRgb}, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(${({ theme }) => theme.primaryRgb}, 0.45);
    gap: 12px;
  }
`;

/* ───────── Component ───────── */
const TopLinks = () => {
  const navigate = useNavigate();

  return (
    <Section>
      <Card>
        <ImageSide>
          <img
            src="https://res.cloudinary.com/dsaex2uux/image/upload/v1763099701/Zafran-school-projects/2023-11-26_gzmtxa.jpg"
            alt="School Science Exhibition Project"
          />
        </ImageSide>

        <ContentSide>
          <Badge>&#9733; Featured in Newspaper</Badge>
          <Title>
            Childhood <span>Science Exhibition</span> Projects
          </Title>
          <StoryText>
            From an early age, I was fascinated by science and innovation. These
            projects were built during my school years for science exhibitions,
            where my innovative ideas caught the attention of judges and even got
            me featured in the local newspaper. This is where my journey as a
            builder and problem-solver truly began.
          </StoryText>
          <ExploreButton onClick={() => navigate("/school-projects-gallery")}>
            View Full Gallery <ArrowForward style={{ fontSize: 18 }} />
          </ExploreButton>
        </ContentSide>
      </Card>
    </Section>
  );
};

export default TopLinks;
